import React, { useEffect, useState, useCallback } from "react";
import { ChevronDown } from "lucide-react";
import api from "@/lib/api";
import { money } from "@/lib/pricing";

const STATUSES = ["Pending", "Confirmed", "Done"];
const STATUS_STYLE = {
  Pending: "text-yellow-400 border-yellow-600",
  Confirmed: "text-blue-400 border-blue-600",
  Done: "text-green-400 border-green-600",
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [open, setOpen] = useState({});

  const load = useCallback(() => { api.get("/admin/orders").then(({ data }) => setOrders(data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => { await api.patch(`/admin/orders/${id}`, { status }); load(); };
  const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

  return (
    <div data-testid="admin-orders">
      <h1 className="font-heading font-black uppercase text-xl text-[#F0EDEA] mb-4">Orders ({orders.length})</h1>
      <div className="space-y-3">
        {orders.map((o) => {
          const t = o.totals || {};
          const isOpen = open[o.id];
          return (
            <div key={o.id} className="bg-[#1C1C1C] border border-[#333] rounded-xl overflow-hidden" data-testid={`order-${o.id}`}>
              <div className="p-3">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div className="font-heading font-bold text-[#F0EDEA] text-sm">#{o.order_number}</div>
                    <div className="text-[#A8A2A0] text-xs">{fmt(o.created_at)}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-heading font-black text-[#E60000]">{money(t.total)}</div>
                    <select value={o.status} onChange={(e) => setStatus(o.id, e.target.value)} className={`mt-1 bg-[#141414] border rounded text-xs px-2 py-1 ${STATUS_STYLE[o.status]}`} data-testid={`order-status-${o.id}`}>
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <button onClick={() => setOpen({ ...open, [o.id]: !isOpen })} className="flex items-center gap-1 text-[#A8A2A0] text-xs mt-2" data-testid={`order-expand-${o.id}`}>
                  <ChevronDown size={14} className={isOpen ? "rotate-180" : ""} /> Details
                </button>
              </div>
              {isOpen && (
                <div className="border-t border-[#333] p-3 text-sm space-y-1">
                  {o.items?.map((it, i) => (
                    <div key={i} className="flex justify-between text-[#A8A2A0]">
                      <span>{it.name} #{it.code} x{it.qty}</span>
                      <span className="text-[#F0EDEA]">{money(it.line_total)}</span>
                    </div>
                  ))}
                  <div className="border-t border-[#333] my-2" />
                  <Row l="Subtotal" v={money(t.subtotal)} />
                  <Row l="Bundle discount" v={`−${money(t.bundle_discount)}`} />
                  <Row l="Fulfillment" v={o.fulfillment_label + (o.fulfillment === "delivery" ? ` +${money(t.delivery_fee)}` : "")} />
                  <Row l={`${o.payment_method} (${o.payment_fee_pct}%)`} v={`+${money(t.payment_fee)}`} />
                  <Row l="Total" v={money(t.total)} bold />
                </div>
              )}
            </div>
          );
        })}
        {orders.length === 0 && <div className="text-[#A8A2A0] text-center py-8">No orders yet.</div>}
      </div>
    </div>
  );
}

function Row({ l, v, bold }) {
  return <div className={`flex justify-between ${bold ? "text-[#F0EDEA] font-bold" : "text-[#A8A2A0]"}`}><span>{l}</span><span className={bold ? "text-[#E60000]" : "text-[#F0EDEA]"}>{v}</span></div>;
}
